/**
 * Set a platform fee on one thing, and see what it does to the price.
 *
 * Shared by the catalogue screen and the approval detail because the decision is identical
 * in both places — only the row it applies to differs. Inheriting is offered as a real
 * choice, not as an empty field: "use the category default" is what most products should
 * do, and an admin needs to be able to go back to it.
 *
 * Every state shows the worked ₹500 example, because a percentage on its own does not tell
 * you what a shopper ends up paying, and that is the number being decided.
 */
import { useState } from 'react';
import { Loader2, Percent, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { CommissionMode } from '@/features/commission/types';

export type CommissionValue = {
  mode: CommissionMode | null;
  rate_pct: number | null;
  amount_inr: number | null;
  /** True when this row sets its own fee rather than inheriting. */
  is_override: boolean;
};

const rupees = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** What ₹500 becomes under a given fee — the number actually being decided. */
function example(mode: CommissionMode, value: number): string {
  const fee = mode === 'FLAT' ? value : Math.round(500 * (value / 100) * 100) / 100;
  return `Vendor lists ₹500 → customer pays ${rupees(500 + fee)}`;
}

export function CommissionEditor({
  value,
  /** Where the fee comes from when this row inherits — "Dry Food default", say. */
  inherited_label,
  /**
   * The fee that ACTUALLY applies right now, inherited or not.
   *
   * Shown as text, never loaded into the input. Prefilling the box would make "inheriting
   * the default" and "pinned at the same number" look identical — and a Save on a prefilled
   * field silently stops the product tracking its category, so a later change to that
   * category quietly never reaches it. The number should be visible; it should not be armed.
   */
  effective,
  on_save,
  saving,
  testid,
  className,
}: {
  value: CommissionValue;
  inherited_label?: string;
  effective?: { mode: CommissionMode | null; rate_pct: number | null; amount_inr: number | null };
  on_save: (next: { mode: CommissionMode | null; rate_pct?: number; amount_inr?: number }) => Promise<unknown>;
  saving?: boolean;
  testid: string;
  className?: string;
}) {
  const [mode, set_mode] = useState<CommissionMode>(value.mode ?? 'RATE');
  const [raw, set_raw] = useState(
    value.mode === 'FLAT' ? String(value.amount_inr ?? '') : value.mode === 'RATE' ? String(value.rate_pct ?? '') : '',
  );
  const [error, set_error] = useState<string | null>(null);
  const [saved, set_saved] = useState(false);

  const numeric = Number(raw);
  const valid = raw !== '' && Number.isFinite(numeric) && numeric >= 0 && (mode === 'FLAT' || numeric <= 100);

  async function save() {
    set_error(null);
    set_saved(false);
    if (!valid) {
      return set_error(mode === 'RATE' ? 'Enter a percentage between 0 and 100.' : 'Enter an amount in rupees.');
    }
    try {
      await on_save({ mode, ...(mode === 'RATE' ? { rate_pct: numeric } : { amount_inr: numeric }) });
      set_saved(true);
    } catch (e) {
      set_error(e instanceof Error ? e.message : 'Could not save the fee.');
    }
  }

  async function inherit() {
    set_error(null);
    set_saved(false);
    try {
      await on_save({ mode: null });
      set_raw('');
      set_saved(true);
    } catch (e) {
      set_error(e instanceof Error ? e.message : 'Could not clear the fee.');
    }
  }

  return (
    <div className={cn('grid gap-1.5', className)} data-testid={`commission-editor-${testid}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Percent className="h-3.5 w-3.5" />
        Platform fee
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-semibold',
            value.is_override ? 'bg-primary/12 text-primary' : 'bg-muted text-muted-foreground',
          )}
          data-testid={`commission-state-${testid}`}
        >
          {value.is_override ? 'set for this product' : (inherited_label ?? 'category default')}
          {/* The inherited number, so "default" is not a fact you have to go and look up. */}
          {!value.is_override && effective && effective.mode && (
            <span className="ml-1" data-testid={`commission-inherited-${testid}`}>
              · {effective.mode === 'FLAT' ? rupees(effective.amount_inr ?? 0) : `${effective.rate_pct ?? 0}%`}
            </span>
          )}
          {!value.is_override && effective && !effective.mode && <span className="ml-1">· no fee</span>}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={mode}
          onChange={(e) => set_mode(e.target.value as CommissionMode)}
          aria-label="Fee type"
          data-testid={`commission-mode-${testid}`}
        >
          <option value="RATE">Percent</option>
          <option value="FLAT">Flat ₹</option>
        </select>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={raw}
          onChange={(e) => set_raw(e.target.value)}
          /*
           * Neutral on purpose. It briefly hinted the inherited number, which truncated to
           * "17 (inh" in a box this narrow — and a bare "17" in the field is the very
           * ambiguity this design avoids. The badge and the "Today:" line below already state
           * the number twice, unclipped.
           */
          placeholder={mode === 'RATE' ? 'e.g. 10' : 'e.g. 40'}
          className="h-9 w-24"
          aria-label="Fee value"
          data-testid={`commission-value-${testid}`}
        />
        <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`commission-save-${testid}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          <span className="ml-1">{saved ? 'Saved' : 'Save'}</span>
        </Button>
        {value.is_override && (
          <Button
            size="sm"
            variant="ghost"
            onClick={inherit}
            disabled={saving}
            title="Go back to the category default"
            data-testid={`commission-inherit-${testid}`}
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Use default
          </Button>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground" data-testid={`commission-example-${testid}`}>
        {valid
          ? example(mode, numeric)
          : effective?.mode
            ? /* What today's fee does, so the empty box still tells you something useful. */
              `Today: ${example(effective.mode, effective.mode === 'FLAT' ? (effective.amount_inr ?? 0) : (effective.rate_pct ?? 0))}`
            : 'No fee applies today. Enter a value to charge one.'}
      </p>
      {error && (
        <p className="text-[11px] font-medium text-destructive" data-testid={`commission-error-${testid}`}>
          {error}
        </p>
      )}
    </div>
  );
}
